import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  MenuItem,
  TextField,
  CircularProgress,
  LinearProgress,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  getSPDataWithParams,
  postSPDataWithParams,
  postSPFormDataWithoutParams,
} from 'src/supportPortal/services/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

const BASE = '/api/secure/cc/support/csm/appointment/bulk-import'
const IMPORT_SOURCES = [{ value: 'EKA_CARE', label: 'EkaCare' }]
const TERMINAL_STATUSES = ['COMPLETED', 'COMPLETED_WITH_FAILURES', 'FAILED']
const ACTIVE_STATUSES = ['PENDING', 'RUNNING']
const STATUS_COLOR = {
  COMPLETED: 'success',
  COMPLETED_WITH_FAILURES: 'warning',
  FAILED: 'error',
  RUNNING: 'info',
  PENDING: 'default',
}

const payload = (resp, key) => resp?.data?.data?.[key]
const isOk = (resp) => resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)

// The nginx edge caps the request body at 200 MB; warn a little under that so an oversized
// file gives a clear message instead of a 413 that the browser surfaces as a CORS error.
const MAX_UPLOAD_BYTES = 195 * 1024 * 1024
const humanSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

export default function AppointmentImportDialog({ open, onClose, clinicBrandId, onImported }) {
  const [importSource, setImportSource] = useState('EKA_CARE')
  const [file, setFile] = useState(null)
  const [locations, setLocations] = useState([])
  const [clinicId, setClinicId] = useState('')
  const [doctors, setDoctors] = useState([])
  const [doctorTeacherId, setDoctorTeacherId] = useState('')
  const [eventTypes, setEventTypes] = useState([])
  const [eventTypeId, setEventTypeId] = useState('')
  const [dayStartHour, setDayStartHour] = useState('10')
  const [slotMinutes, setSlotMinutes] = useState('')

  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [starting, setStarting] = useState(false)
  const [retryingId, setRetryingId] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const [jobs, setJobs] = useState([])
  const [loadingJobs, setLoadingJobs] = useState(false)

  const completedRef = useRef(new Set())
  const initializedRef = useRef(false)

  const describeError = (respOrErr, fallback) => {
    const status = respOrErr?.response?.status ?? respOrErr?.status
    const apiMsg = respOrErr?.data?.message || respOrErr?.response?.data?.message
    // eslint-disable-next-line no-console
    console.error('Appointment import request failed:', { status, response: respOrErr })
    if (status === 404) {
      return 'Import API not found (HTTP 404) — the backend with appointment-import support is not deployed to this environment yet.'
    }
    if (apiMsg) return status ? `${apiMsg} (HTTP ${status})` : apiMsg
    if (status) return `${fallback} (HTTP ${status})`
    return fallback
  }

  const loadJobs = useCallback(async () => {
    if (!clinicBrandId) return
    setLoadingJobs(true)
    try {
      const resp = await getSPDataWithParams(`${BASE}/jobs`, { clinicBrandId, page: 1, pageSize: 15 })
      if (isOk(resp)) {
        const list = payload(resp, 'appointmentBulkImportJobs') || []
        setJobs(list)
        let refreshPatients = false
        list.forEach((job) => {
          if (TERMINAL_STATUSES.includes(job.status) && !completedRef.current.has(job.id)) {
            completedRef.current.add(job.id)
            if (initializedRef.current && (job.eventsCreated || 0) > 0) refreshPatients = true
          }
        })
        initializedRef.current = true
        if (refreshPatients) onImported?.()
      }
    } finally {
      setLoadingJobs(false)
    }
  }, [clinicBrandId, onImported])

  // Load locations + doctors + job history when the dialog opens.
  useEffect(() => {
    if (!open || !clinicBrandId) return undefined
    let active = true
    getSPDataWithParams(`${BASE}/clinic-locations`, { clinicBrandId }).then((resp) => {
      if (!active) return
      const locs = isOk(resp) ? payload(resp, 'clinicLocations') || [] : []
      setLocations(locs)
      if (locs.length === 1) setClinicId(locs[0].id)
    })
    getSPDataWithParams(`${BASE}/doctors`, { clinicBrandId }).then((resp) => {
      if (!active) return
      const docs = isOk(resp) ? payload(resp, 'doctors') || [] : []
      setDoctors(docs)
      if (docs.length === 1) setDoctorTeacherId(docs[0].id)
    })
    loadJobs()
    return () => {
      active = false
    }
  }, [open, clinicBrandId, loadJobs])

  // Load consultation event types once a clinic + doctor are chosen.
  useEffect(() => {
    if (!open || !clinicBrandId || !clinicId || !doctorTeacherId) {
      setEventTypes([])
      return undefined
    }
    let active = true
    getSPDataWithParams(`${BASE}/event-types`, { clinicBrandId, clinicId, doctorTeacherId }).then((resp) => {
      if (!active) return
      const types = isOk(resp) ? payload(resp, 'eventTypes') || [] : []
      setEventTypes(types)
      if (types.length === 1) setEventTypeId(types[0].id)
    })
    return () => {
      active = false
    }
  }, [open, clinicBrandId, clinicId, doctorTeacherId])

  // Poll the log while any job is active.
  useEffect(() => {
    if (!open) return undefined
    if (!jobs.some((j) => ACTIVE_STATUSES.includes(j.status))) return undefined
    const id = setInterval(loadJobs, 3000)
    return () => clearInterval(id)
  }, [open, jobs, loadJobs])

  const buildFormData = () => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('clinicBrandId', clinicBrandId)
    if (clinicId) fd.append('clinicId', clinicId)
    if (doctorTeacherId) fd.append('doctorTeacherId', doctorTeacherId)
    if (eventTypeId) fd.append('eventTypeId', eventTypeId)
    if (dayStartHour !== '' && dayStartHour != null) fd.append('dayStartHour', String(parseInt(dayStartHour, 10)))
    if (slotMinutes !== '' && slotMinutes != null) fd.append('slotMinutes', String(parseInt(slotMinutes, 10)))
    return fd
  }

  const handlePreview = async () => {
    setError('')
    setNotice('')
    setPreviewing(true)
    try {
      const resp = await postSPFormDataWithoutParams(`${BASE}/preview`, buildFormData())
      if (isOk(resp)) setPreview(payload(resp, 'appointmentImportPreview'))
      else setError(describeError(resp, 'Could not preview the file.'))
    } catch (e) {
      setError(describeError(e, 'Could not preview the file.'))
    } finally {
      setPreviewing(false)
    }
  }

  const handleStart = async () => {
    setError('')
    setNotice('')
    setStarting(true)
    try {
      const resp = await postSPFormDataWithoutParams(`${BASE}/upload`, buildFormData())
      if (isOk(resp)) {
        const job = payload(resp, 'appointmentBulkImportJob')
        setNotice(
          `Import started for "${job?.originalFilename || file?.name}" (${job?.totalRows ?? '?'} appointments). ` +
            'It runs in the background — you can close this dialog; progress shows in the log below.',
        )
        setPreview(null)
        setFile(null)
        await loadJobs()
      } else {
        setError(describeError(resp, 'Could not start the import.'))
      }
    } catch (e) {
      setError(describeError(e, 'Could not start the import.'))
    } finally {
      setStarting(false)
    }
  }

  const handleRetry = async (jobId) => {
    setError('')
    setRetryingId(jobId)
    try {
      const resp = await postSPDataWithParams(`${BASE}/retry/${jobId}`, {})
      if (isOk(resp)) await loadJobs()
      else setError(describeError(resp, 'Could not retry the job.'))
    } catch (e) {
      setError(describeError(e, 'Could not retry the job.'))
    } finally {
      setRetryingId('')
    }
  }

  const handleClose = () => {
    setError('')
    setNotice('')
    setPreview(null)
    onClose?.()
  }

  const canSubmit = file && clinicId && doctorTeacherId && importSource && !starting && !previewing

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Import Appointments
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          New import
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Import source"
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {IMPORT_SOURCES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Clinic location"
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.nickName || loc.title || loc.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Doctor"
              value={doctorTeacherId}
              onChange={(e) => setDoctorTeacherId(e.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
            >
              {doctors.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.label || d.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Event type (optional)"
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
              helperText={eventTypes.length === 0 ? 'Auto-resolved if left blank' : 'Consultation type'}
            >
              <MenuItem value="">
                <em>Auto (consultation)</em>
              </MenuItem>
              {eventTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label || t.id}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              type="number"
              label="Day start time (hour)"
              value={dayStartHour}
              onChange={(e) => setDayStartHour(e.target.value)}
              inputProps={{ min: 0, max: 23, step: 1 }}
              sx={{ minWidth: 180 }}
              helperText="First event each day (clinic local, 24h). Default 10 = 10:00 AM."
            />
            <TextField
              type="number"
              label="Slot spacing (min)"
              value={slotMinutes}
              onChange={(e) => setSlotMinutes(e.target.value)}
              inputProps={{ min: 0, max: 1440, step: 5 }}
              sx={{ minWidth: 180 }}
              helperText="Gap between same-day events. Blank = event duration."
            />
            <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
              Times use the clinic&apos;s local zone (Asia/Kolkata, +5:30). Same-day appointments are
              scheduled sequentially so none collide.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              {file ? file.name : 'Choose XLSX file'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const picked = e.target.files?.[0] || null
                  if (picked && picked.size > MAX_UPLOAD_BYTES) {
                    setError(
                      `"${picked.name}" is ${humanSize(picked.size)}, over the ${humanSize(MAX_UPLOAD_BYTES)} upload limit. ` +
                        'Split it into smaller batches and import them separately.',
                    )
                    setFile(null)
                    setPreview(null)
                    e.target.value = ''
                    return
                  }
                  setError('')
                  setFile(picked)
                  setPreview(null)
                }}
              />
            </Button>
            <Button variant="contained" onClick={handleStart} disabled={!canSubmit}>
              {starting ? <CircularProgress size={20} /> : 'Start Import'}
            </Button>
            <Tooltip title="Optional dry run — validates the file without creating anything">
              <span>
                <Button variant="text" onClick={handlePreview} disabled={!canSubmit}>
                  {previewing ? <CircularProgress size={18} /> : 'Preview (optional)'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Collapse in={Boolean(preview)}>
            {preview && (
              <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                {!preview.brandKycApproved && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    This clinic brand&apos;s KYC is not approved yet — the import will be rejected until it is.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" label={`${preview.totalRows} rows`} />
                  <Chip size="small" color="success" label={`${preview.importableRows} importable`} />
                  <Chip size="small" label={`${preview.withPrescriptionUrl} with prescription`} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Appointment</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>UHID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Rx</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.sample || []).map((r) => (
                      <TableRow key={r.rowNumber}>
                        <TableCell>{r.rowNumber}</TableCell>
                        <TableCell>{r.appointmentId}</TableCell>
                        <TableCell>{r.patientName || '—'}</TableCell>
                        <TableCell>{r.uhId || '—'}</TableCell>
                        <TableCell>{r.startTime ? String(r.startTime).slice(0, 10) : '—'}</TableCell>
                        <TableCell>{r.hasPrescriptionUrl ? 'yes' : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Collapse>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Recent imports</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadJobs} disabled={loadingJobs}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {loadingJobs ? 'Loading…' : 'No appointment imports yet for this brand.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Events</TableCell>
                <TableCell align="right">New patients</TableCell>
                <TableCell align="right">Rx ok</TableCell>
                <TableCell align="right">Rx fail</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell>When</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const pct =
                  job.totalRows > 0 ? Math.round((job.processedRows / job.totalRows) * 100) : 0
                const isActive = ACTIVE_STATUSES.includes(job.status)
                const canRetry =
                  !isActive && (job.rowsFailed > 0 || job.prescriptionsFailed > 0) && job.sourceFileS3Key
                return (
                  <TableRow key={job.id}>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={job.originalFilename || ''}>
                        <span>{job.originalFilename || job.id}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[job.status] || 'default'} label={job.status} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      {isActive ? (
                        <Box>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.processedRows}/{job.totalRows}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {job.processedRows}/{job.totalRows}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{job.eventsCreated || 0}</TableCell>
                    <TableCell align="right">{job.patientsAutoCreated || 0}</TableCell>
                    <TableCell align="right">{job.prescriptionsAttached || 0}</TableCell>
                    <TableCell align="right">{job.prescriptionsFailed || 0}</TableCell>
                    <TableCell align="right">{job.rowsFailed || 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {job.createdTime ? `${job.createdTime} UTC` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {canRetry && (
                        <Tooltip title="Retry failed rows (idempotent)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRetry(job.id)}
                              disabled={retryingId === job.id}
                            >
                              {retryingId === job.id ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
